export const COLOR_MAP = {
  c1: '#858AF9',
  c2: '#02AD84',
  c3: '#409CFF',
  c4: '#FCB734',
  c5: '#AEAEB2',
  ricorrenti: '#E040A0',
  disdetteCliente: '#FF6A88',
  disdetteSalone: '#409CFF',
  queue: '#8549FE',
  recovered: '#FCB734',
};

const hexToRgb = (hex) => {
  const normalized = hex.replace('#', '');
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

const rgbToHex = (r, g, b) =>
  `#${[r, g, b]
    .map((value) =>
      Math.max(0, Math.min(255, Math.round(value)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;

/** Sfumature del colore categoria per distinguere le poltrone (count dinamico). */
export const buildPoltronaShades = (baseColor, count) => {
  if (count <= 0) return [];
  if (count === 1) return [baseColor];

  const { r, g, b } = hexToRgb(baseColor);
  const maxMix = 0.52;

  return Array.from({ length: count }, (_, index) => {
    const mix = (maxMix * index) / (count - 1);
    return rgbToHex(r + (255 - r) * mix, g + (255 - g) * mix, b + (255 - b) * mix);
  });
};

export const POLTRONE_OPTIONS = [
  { value: 'tutte', label: 'Tutte le poltrone' },
  { value: 'p1', label: 'Marco' },
  { value: 'p2', label: 'Luca' },
  { value: 'p3', label: 'Sara' },
  { value: 'p4', label: 'Giulia' },
];

export const PERIOD_OPTIONS = [
  'Oggi',
  'Questa settimana',
  'Questo mese',
  'Anno corrente',
  'Personalizzato…',
];

export const COMPARE_OPTIONS = [
  'Nessun confronto',
  'Ieri',
  'Anno precedente',
  'Anno precedente (corrispondente)',
  'Personalizzato…',
];

export const PERFORMANCE_PERIODO_OPTIONS = [
  { value: 'corrente', label: 'Corrente' },
  { value: '3mesi', label: 'Ogni 3 mesi' },
  { value: '6mesi', label: 'Ogni 6 mesi' },
];

/** Baseline mensile per poltrona — gli altri periodi scalano da qui */
const BASE_POLTRONE = {
  tutte: {
    fatturato: 10800,
    uscite: 4416,
    prenotazioni: 337,
    clientiAttivi: 156,
    clientiTotali: 380,
    canali: [
      ['App', 42, 'c1'],
      ['Salone', 28, 'c2'],
      ['Google', 15, 'c3'],
      ['Web', 9, 'ricorrenti'],
      ['Ricorrenti', 6, 'c4'],
    ],
    servizi: [
      ['Taglio', 45, 'c1'],
      ['Barba', 25, 'c2'],
      ['Taglio + Barba', 20, 'c3'],
      ['Colore', 6, 'ricorrenti'],
      ['Altro', 4, 'c4'],
    ],
  },
  p1: {
    fatturato: 3200,
    uscite: 1306,
    prenotazioni: 98,
    clientiAttivi: 46,
    clientiTotali: 112,
    canali: [
      ['App', 50, 'c1'],
      ['Salone', 22, 'c2'],
      ['Google', 12, 'c3'],
      ['Web', 10, 'ricorrenti'],
      ['Ricorrenti', 6, 'c4'],
    ],
    servizi: [
      ['Taglio', 40, 'c1'],
      ['Barba', 30, 'c2'],
      ['Taglio + Barba', 22, 'c3'],
      ['Colore', 5, 'ricorrenti'],
      ['Altro', 3, 'c4'],
    ],
  },
  p2: {
    fatturato: 2100,
    uscite: 1085,
    prenotazioni: 72,
    clientiAttivi: 34,
    clientiTotali: 86,
    canali: [
      ['App', 35, 'c1'],
      ['Salone', 33, 'c2'],
      ['Google', 18, 'c3'],
      ['Web', 8, 'ricorrenti'],
      ['Ricorrenti', 6, 'c4'],
    ],
    servizi: [
      ['Taglio', 50, 'c1'],
      ['Barba', 20, 'c2'],
      ['Taglio + Barba', 18, 'c3'],
      ['Colore', 7, 'ricorrenti'],
      ['Altro', 5, 'c4'],
    ],
  },
  p3: {
    fatturato: 1900,
    uscite: 820,
    prenotazioni: 64,
    clientiAttivi: 30,
    clientiTotali: 78,
    canali: [
      ['App', 38, 'c1'],
      ['Salone', 30, 'c2'],
      ['Google', 16, 'c3'],
      ['Web', 10, 'ricorrenti'],
      ['Ricorrenti', 6, 'c4'],
    ],
    servizi: [
      ['Taglio', 48, 'c1'],
      ['Barba', 22, 'c2'],
      ['Taglio + Barba', 18, 'c3'],
      ['Colore', 7, 'ricorrenti'],
      ['Altro', 5, 'c4'],
    ],
  },
  p4: {
    fatturato: 3600,
    uscite: 1205,
    prenotazioni: 103,
    clientiAttivi: 52,
    clientiTotali: 124,
    canali: [
      ['App', 44, 'c1'],
      ['Salone', 24, 'c2'],
      ['Google', 16, 'c3'],
      ['Web', 10, 'ricorrenti'],
      ['Ricorrenti', 6, 'c4'],
    ],
    servizi: [
      ['Taglio', 43, 'c1'],
      ['Barba', 24, 'c2'],
      ['Taglio + Barba', 21, 'c3'],
      ['Colore', 7, 'ricorrenti'],
      ['Altro', 5, 'c4'],
    ],
  },
};

const PERIOD_SCALE = {
  Oggi: { money: 0.09, bookings: 0.06 },
  'Questa settimana': { money: 0.27, bookings: 0.24 },
  'Questo mese': { money: 1, bookings: 1 },
  'Anno corrente': { money: 7.4, bookings: 6.8 },
  'Personalizzato…': { money: 0.48, bookings: 0.42 },
};

/** Delta mock vs confronto selezionato (null = nascosto) */
const COMPARE_DELTA = {
  'Nessun confronto': null,
  Ieri: { pct: 4, direction: 'up' },
  'Anno precedente': { pct: 12, direction: 'up' },
  'Anno precedente (corrispondente)': { pct: -3, direction: 'down' },
  'Personalizzato…': { pct: 7, direction: 'up' },
};

const QUEUE_BY_PERIOD = {
  Oggi: [{ label: 'Oggi', queue: 4, recovered: 2 }],
  'Questa settimana': [
    { label: 'Lun', queue: 3, recovered: 2 },
    { label: 'Mar', queue: 5, recovered: 3 },
    { label: 'Mer', queue: 4, recovered: 2 },
    { label: 'Gio', queue: 6, recovered: 4 },
    { label: 'Ven', queue: 8, recovered: 5 },
    { label: 'Sab', queue: 7, recovered: 4 },
    { label: 'Dom', queue: 2, recovered: 1 },
  ],
  'Questo mese': [
    { label: 'Sett 1', queue: 12, recovered: 7 },
    { label: 'Sett 2', queue: 9, recovered: 6 },
    { label: 'Sett 3', queue: 15, recovered: 10 },
    { label: 'Sett 4', queue: 8, recovered: 5 },
  ],
  'Anno corrente': [
    { label: 'Gen', queue: 38, recovered: 22 },
    { label: 'Feb', queue: 34, recovered: 20 },
    { label: 'Mar', queue: 41, recovered: 26 },
    { label: 'Apr', queue: 36, recovered: 21 },
    { label: 'Mag', queue: 44, recovered: 28 },
    { label: 'Giu', queue: 48, recovered: 30 },
  ],
  'Personalizzato…': [
    { label: 'P1', queue: 10, recovered: 6 },
    { label: 'P2', queue: 14, recovered: 9 },
    { label: 'P3', queue: 11, recovered: 7 },
  ],
};

const GIORNI_BY_PERIOD = {
  Oggi: [
    { label: 'Mattina', count: 6 },
    { label: 'Pomeriggio', count: 9 },
    { label: 'Sera', count: 4 },
  ],
  'Questa settimana': [
    { label: 'Lun', count: 14 },
    { label: 'Mar', count: 18 },
    { label: 'Mer', count: 22 },
    { label: 'Gio', count: 19 },
    { label: 'Ven', count: 31 },
    { label: 'Sab', count: 38 },
    { label: 'Dom', count: 5 },
  ],
  'Questo mese': [
    { label: 'Lun', count: 52 },
    { label: 'Mar', count: 61 },
    { label: 'Mer', count: 74 },
    { label: 'Gio', count: 68 },
    { label: 'Ven', count: 95 },
    { label: 'Sab', count: 112 },
    { label: 'Dom', count: 18 },
  ],
  'Anno corrente': [
    { label: 'Lun', count: 410 },
    { label: 'Mar', count: 480 },
    { label: 'Mer', count: 520 },
    { label: 'Gio', count: 495 },
    { label: 'Ven', count: 710 },
    { label: 'Sab', count: 860 },
    { label: 'Dom', count: 140 },
  ],
  'Personalizzato…': [
    { label: 'Lun', count: 28 },
    { label: 'Mar', count: 33 },
    { label: 'Mer', count: 41 },
    { label: 'Gio', count: 36 },
    { label: 'Ven', count: 55 },
    { label: 'Sab', count: 62 },
    { label: 'Dom', count: 9 },
  ],
};

const RISCHIO_BY_PERIOD = {
  Oggi: [
    { nome: 'Paolo Rossi', dettaglio: '52 giorni fa' },
    { nome: 'Anna Bianchi', dettaglio: '47 giorni fa' },
  ],
  'Questa settimana': [
    { nome: 'Paolo Rossi', dettaglio: '52 giorni fa' },
    { nome: 'Anna Bianchi', dettaglio: '47 giorni fa' },
    { nome: 'Davide Conti', dettaglio: '45 giorni fa' },
  ],
  'Questo mese': [
    { nome: 'Paolo Rossi', dettaglio: '52 giorni fa' },
    { nome: 'Anna Bianchi', dettaglio: '47 giorni fa' },
    { nome: 'Davide Conti', dettaglio: '45 giorni fa' },
    { nome: 'Elena Ferri', dettaglio: '41 giorni fa' },
  ],
  'Anno corrente': [
    { nome: 'Paolo Rossi', dettaglio: '52 giorni fa' },
    { nome: 'Anna Bianchi', dettaglio: '47 giorni fa' },
    { nome: 'Davide Conti', dettaglio: '45 giorni fa' },
    { nome: 'Elena Ferri', dettaglio: '41 giorni fa' },
    { nome: 'Luca Moretti', dettaglio: '39 giorni fa' },
  ],
  'Personalizzato…': [
    { nome: 'Anna Bianchi', dettaglio: '47 giorni fa' },
    { nome: 'Elena Ferri', dettaglio: '41 giorni fa' },
  ],
};

const RISCHIO_COUNT_BY_PERIOD = {
  Oggi: 2,
  'Questa settimana': 5,
  'Questo mese': 18,
  'Anno corrente': 64,
  'Personalizzato…': 8,
};

const DISDETTE_BY_PERIOD = {
  Oggi: { totale: 2, daCliente: 1, daSalone: 1 },
  'Questa settimana': { totale: 9, daCliente: 6, daSalone: 3 },
  'Questo mese': { totale: 28, daCliente: 19, daSalone: 9 },
  'Anno corrente': { totale: 186, daCliente: 124, daSalone: 62 },
  'Personalizzato…': { totale: 14, daCliente: 9, daSalone: 5 },
};

const PERICOLOSI_BY_PERIOD = {
  Oggi: [{ nome: 'Marco Villa', dettaglio: '4 no-show' }],
  'Questa settimana': [
    { nome: 'Marco Villa', dettaglio: '4 no-show' },
    { nome: 'Giorgia Neri', dettaglio: '3 disdette' },
  ],
  'Questo mese': [
    { nome: 'Marco Villa', dettaglio: '4 no-show' },
    { nome: 'Giorgia Neri', dettaglio: '3 disdette' },
    { nome: 'Tommaso Greco', dettaglio: '3 no-show' },
  ],
  'Anno corrente': [
    { nome: 'Marco Villa', dettaglio: '7 no-show' },
    { nome: 'Giorgia Neri', dettaglio: '6 disdette' },
    { nome: 'Tommaso Greco', dettaglio: '5 no-show' },
    { nome: 'Irene Sala', dettaglio: '4 disdette' },
  ],
  'Personalizzato…': [
    { nome: 'Giorgia Neri', dettaglio: '3 disdette' },
    { nome: 'Tommaso Greco', dettaglio: '3 no-show' },
  ],
};

export const performanceByPeriod = {
  corrente: { fatturato: 10800, prenotazioni: 337, clientiAttivi: 156 },
  '3mesi': { fatturato: 31400, prenotazioni: 980, clientiAttivi: 210 },
  '6mesi': { fatturato: 61200, prenotazioni: 1850, clientiAttivi: 280 },
};

const roundMoney = (n) => Math.round(n);
const roundCount = (n) => Math.max(1, Math.round(n));

const getPoltronaRatios = (poltrona) => {
  if (poltrona === 'tutte') {
    return { money: 1, bookings: 1, clients: 1, anagrafica: 1 };
  }

  const chair = BASE_POLTRONE[poltrona] || BASE_POLTRONE.tutte;
  const all = BASE_POLTRONE.tutte;

  return {
    money: chair.fatturato / all.fatturato,
    bookings: chair.prenotazioni / all.prenotazioni,
    clients: chair.clientiAttivi / all.clientiAttivi,
    anagrafica: chair.clientiTotali / all.clientiTotali,
  };
};

const scaleQueue = (queue, factor) =>
  queue.map((item) => ({
    ...item,
    queue: roundCount(item.queue * factor),
    recovered: roundCount(item.recovered * factor),
  }));

const scaleGiorni = (giorni, factor) =>
  giorni.map((item) => ({
    ...item,
    count: roundCount(item.count * factor),
  }));

const scaleDisdette = (disdette, factor) => {
  const totale = roundCount(disdette.totale * factor);
  const daCliente = Math.min(totale, roundCount(disdette.daCliente * factor));
  return {
    totale,
    daCliente,
    daSalone: Math.max(0, totale - daCliente),
  };
};

const buildCompareDeltas = (compare) => {
  const base = COMPARE_DELTA[compare] ?? null;
  if (!base) return null;

  const { direction } = base;

  return {
    fatturato: base,
    entrate: base,
    uscite: { pct: Math.max(1, base.pct - 2), direction },
    utile: { pct: base.pct + 3, direction },
    scontrinoMedio: { pct: Math.max(1, Math.round(base.pct / 2)), direction },
    clientiAttivi: { pct: Math.max(1, base.pct - 1), direction },
    prenotazioni: base,
  };
};

const invertDirection = (direction) => (direction === 'up' ? 'down' : 'up');

const buildCategoryDeltas = (rows, compare) => {
  const base = COMPARE_DELTA[compare] ?? null;
  if (!base) return null;

  return rows.reduce((acc, [label], index) => {
    const ppChange =
      (index % 2 === 0 ? 1 : -1) * Math.max(1, Math.round(base.pct / (index + 1.5)));
    acc[label] = {
      pct: Math.abs(ppChange),
      direction: ppChange >= 0 ? base.direction : invertDirection(base.direction),
    };
    return acc;
  }, {});
};

const buildQueueDeltas = (compare) => {
  const base = COMPARE_DELTA[compare] ?? null;
  if (!base) return null;

  return {
    queue: base,
    recovered: { pct: Math.max(1, base.pct - 1), direction: base.direction },
  };
};

const buildDisdetteDeltas = (compare) => {
  const base = COMPARE_DELTA[compare] ?? null;
  if (!base) return null;

  return {
    totale: base,
    daCliente: { pct: base.pct + 1, direction: base.direction },
    daSalone: {
      pct: Math.max(1, base.pct - 2),
      direction: invertDirection(base.direction),
    },
  };
};

const buildTeamStats = (period) => {
  const scale = PERIOD_SCALE[period] || PERIOD_SCALE['Questo mese'];
  return POLTRONE_OPTIONS.filter((opt) => opt.value !== 'tutte')
    .map((opt) => {
      const chairBase = BASE_POLTRONE[opt.value];
      if (!chairBase) return null;
      return {
        id: opt.value,
        name: opt.label,
        fatturato: roundMoney(chairBase.fatturato * scale.money),
        prenotazioni: roundCount(chairBase.prenotazioni * scale.bookings),
      };
    })
    .filter(Boolean);
};

const shiftDistribution = (rows, period) => {
  const shift =
    period === 'Oggi' ? 4 : period === 'Questa settimana' ? 2 : period === 'Anno corrente' ? -3 : 0;
  if (!shift) return rows.map(([l, p, c]) => [l, p, c]);

  const next = rows.map(([label, pct, color], index) => {
    if (index === 0) return [label, Math.min(70, Math.max(15, pct + shift)), color];
    if (index === 1) return [label, Math.min(50, Math.max(10, pct - Math.round(shift / 2))), color];
    return [label, pct, color];
  });

  const total = next.reduce((sum, [, pct]) => sum + pct, 0);
  const diff = 100 - total;
  next[next.length - 1][1] = Math.max(1, next[next.length - 1][1] + diff);
  return next;
};

const buildCategoryPoltronaSegments = (label, field, period, categoryPct) => {
  const poltrone = POLTRONE_OPTIONS.filter((opt) => opt.value !== 'tutte');
  const weights = poltrone.map((opt) => {
    const chairRows = shiftDistribution(BASE_POLTRONE[opt.value][field], period);
    const entry = chairRows.find(([rowLabel]) => rowLabel === label);
    const chairPct = entry ? entry[1] : 0;
    const bookings = BASE_POLTRONE[opt.value].prenotazioni;
    return {
      id: opt.value,
      name: opt.label,
      weight: bookings * (chairPct / 100),
    };
  });

  const totalWeight = weights.reduce((sum, item) => sum + item.weight, 0) || 1;

  return weights.map((item) => ({
    id: item.id,
    name: item.name,
    sharePct: Math.round((item.weight / totalWeight) * 100),
    barPct: (item.weight / totalWeight) * categoryPct,
  }));
};

const buildCategorySegmentsMap = (rows, field, period) =>
  rows.reduce((acc, [label, pct]) => {
    acc[label] = buildCategoryPoltronaSegments(label, field, period, pct);
    return acc;
  }, {});

const distributeTotalByPoltrone = (total, selectedPoltrona = 'tutte') => {
  const poltrone = POLTRONE_OPTIONS.filter((opt) => opt.value !== 'tutte');

  if (selectedPoltrona !== 'tutte') {
    const match = POLTRONE_OPTIONS.find((opt) => opt.value === selectedPoltrona);
    return [
      {
        id: selectedPoltrona,
        name: match?.label || selectedPoltrona,
        value: total,
        sharePct: 100,
      },
    ];
  }

  const weights = poltrone.map((opt) => ({
    id: opt.value,
    name: opt.label,
    weight: BASE_POLTRONE[opt.value].prenotazioni,
  }));
  const totalWeight = weights.reduce((sum, item) => sum + item.weight, 0) || 1;

  const segments = weights.map((item) => ({
    id: item.id,
    name: item.name,
    value: Math.max(0, Math.round((item.weight / totalWeight) * total)),
  }));

  const assigned = segments.reduce((sum, item) => sum + item.value, 0);
  if (segments.length) segments[0].value += total - assigned;

  return segments.map((item) => ({
    ...item,
    sharePct: total > 0 ? Math.round((item.value / total) * 100) : 0,
  }));
};

const buildQueueSummary = (queueRows) => {
  const totalQueue = queueRows.reduce((sum, row) => sum + row.queue, 0);
  const totalRecovered = queueRows.reduce((sum, row) => sum + row.recovered, 0);
  const total = totalQueue + totalRecovered || 1;

  return {
    totalQueue,
    totalRecovered,
    items: [
      ['In coda', Math.round((totalQueue / total) * 100), 'queue'],
      ['Recuperati', Math.round((totalRecovered / total) * 100), 'recovered'],
    ],
    queueSegments: distributeTotalByPoltrone(totalQueue),
    recoveredSegments: distributeTotalByPoltrone(totalRecovered),
  };
};

const buildQueueRowsWithSegments = (queueRows, poltrona) =>
  queueRows.map((row) => ({
    ...row,
    queueSegments: distributeTotalByPoltrone(row.queue, poltrona),
    recoveredSegments: distributeTotalByPoltrone(row.recovered, poltrona),
  }));

const buildDisdetteSummaryItems = (disdette) => {
  const { totale, daCliente, daSalone } = disdette;
  if (!totale) return [];

  return [
    ['Da cliente', Math.round(safeDivide(daCliente, totale) * 100), 'disdetteCliente'],
    ['Da salone', Math.round(safeDivide(daSalone, totale) * 100), 'disdetteSalone'],
  ];
};

const buildDisdettePoltronaSegments = (disdette, poltrona) => {
  if (poltrona !== 'tutte') return null;

  return {
    daCliente: distributeTotalByPoltrone(disdette.daCliente, poltrona),
    daSalone: distributeTotalByPoltrone(disdette.daSalone, poltrona),
  };
};

export const buildDisdetteDonutGradient = (disdette, segments, showPoltronaSplit) => {
  const { totale, daCliente, daSalone } = disdette;
  if (!totale) return 'conic-gradient(#E0E0E0 0% 100%)';

  const pctCliente = Math.round(safeDivide(daCliente, totale) * 100);
  const pctSalone = Math.max(0, 100 - pctCliente);

  if (!showPoltronaSplit || !segments) {
    return buildConicGradient([
      ['Da cliente', pctCliente, 'disdetteCliente'],
      ['Da salone', pctSalone, 'disdetteSalone'],
    ]);
  }

  let acc = 0;
  const stops = [];
  const categories = [
    { segs: segments.daCliente, pct: pctCliente, color: COLOR_MAP.disdetteCliente },
    { segs: segments.daSalone, pct: pctSalone, color: COLOR_MAP.disdetteSalone },
  ];

  categories.forEach(({ segs, pct, color }) => {
    const shades = buildPoltronaShades(color, segs.length);
    segs.forEach((seg, index) => {
      const slicePct = (seg.sharePct / 100) * pct;
      if (slicePct <= 0) return;
      stops.push(`${shades[index]} ${acc}% ${acc + slicePct}%`);
      acc += slicePct;
    });
  });

  return `conic-gradient(${stops.join(', ')})`;
};

const buildFinEntrateBreakdown = (period, poltrona) => {
  const scale = PERIOD_SCALE[period] || PERIOD_SCALE['Questo mese'];
  const poltrone =
    poltrona === 'tutte'
      ? POLTRONE_OPTIONS.filter((opt) => opt.value !== 'tutte')
      : POLTRONE_OPTIONS.filter((opt) => opt.value === poltrona);

  const rows = poltrone.map((opt, index) => ({
    id: opt.value,
    label: opt.label,
    amount: roundMoney(BASE_POLTRONE[opt.value].fatturato * scale.money),
    colorKey: `c${(index % 5) + 1}`,
  }));

  const total = rows.reduce((sum, row) => sum + row.amount, 0) || 1;
  return rows.map((row) => ({
    ...row,
    pct: Math.round((row.amount / total) * 100),
  }));
};

const USCITE_TIPO_DISTRIBUTION = [
  ['Staff', 0.34, 'c1'],
  ['Affitto', 0.22, 'c2'],
  ['Bollette', 0.11, 'c3'],
  ['Materiali', 0.14, 'c4'],
  ['Manutenzione', 0.06, 'c5'],
  ['Marketing', 0.04, 'c1'],
  ['Tasse / Commissioni', 0.05, 'c2'],
  ['Altro', 0.04, 'c3'],
];

const buildFinUsciteBreakdown = (totalUscite) => {
  const rows = USCITE_TIPO_DISTRIBUTION.map(([label, ratio, colorKey]) => ({
    id: label,
    label,
    amount: roundMoney(totalUscite * ratio),
    colorKey,
    pct: Math.round(ratio * 100),
  }));

  const assigned = rows.reduce((sum, row) => sum + row.amount, 0);
  if (rows.length) rows[0].amount += totalUscite - assigned;

  return rows.filter((row) => row.amount > 0);
};

const COMPARE_LABEL = {
  'Nessun confronto': null,
  Ieri: 'vs ieri',
  'Anno precedente': 'vs anno precedente',
  'Anno precedente (corrispondente)': 'vs anno precedente (corrispondente)',
  'Personalizzato…': 'vs periodo personalizzato',
};

/**
 * Restituisce lo scope mock per i filtri selezionati.
 * TODO: sostituire con le API reali quando disponibili.
 */
export const getMockStatisticsScope = ({
  poltrona = 'tutte',
  period = 'Questo mese',
  compare = 'Nessun confronto',
} = {}) => {
  const base = BASE_POLTRONE[poltrona] || BASE_POLTRONE.tutte;
  const scale = PERIOD_SCALE[period] || PERIOD_SCALE['Questo mese'];
  const poltronaRatios = getPoltronaRatios(poltrona);

  const fatturato = roundMoney(base.fatturato * scale.money);
  const uscite = roundMoney(base.uscite * scale.money);
  const prenotazioni = roundCount(base.prenotazioni * scale.bookings);
  const clientiAttivi = roundCount(base.clientiAttivi * scale.bookings);
  const clientiTotali = roundCount(base.clientiTotali * poltronaRatios.anagrafica);
  const clientiRischioAbbandono = roundCount(
    (RISCHIO_COUNT_BY_PERIOD[period] ?? RISCHIO_COUNT_BY_PERIOD['Questo mese']) *
      poltronaRatios.clients,
  );
  const clientiPericolosiCount = roundCount(
    (PERICOLOSI_BY_PERIOD[period] || PERICOLOSI_BY_PERIOD['Questo mese']).length *
      poltronaRatios.clients,
  );
  const disdette = scaleDisdette(
    DISDETTE_BY_PERIOD[period] || DISDETTE_BY_PERIOD['Questo mese'],
    poltronaRatios.bookings,
  );
  const entrate = fatturato;
  const canali = shiftDistribution(base.canali, period);
  const servizi = shiftDistribution(base.servizi, period);
  const deltas = buildCompareDeltas(compare);
  const queueRows = buildQueueRowsWithSegments(
    scaleQueue(
      QUEUE_BY_PERIOD[period] || QUEUE_BY_PERIOD['Questo mese'],
      poltronaRatios.bookings,
    ),
    poltrona,
  );

  return {
    fatturato,
    entrate,
    uscite,
    prenotazioni,
    clientiAttivi,
    clientiTotali,
    clientiRischioAbbandono,
    clientiPericolosiCount,
    disdette,
    team: buildTeamStats(period),
    canali,
    servizi,
    canaliSegments:
      poltrona === 'tutte' ? buildCategorySegmentsMap(canali, 'canali', period) : null,
    serviziSegments:
      poltrona === 'tutte' ? buildCategorySegmentsMap(servizi, 'servizi', period) : null,
    canaliDeltas: buildCategoryDeltas(canali, compare),
    serviziDeltas: buildCategoryDeltas(servizi, compare),
    queueDeltas: buildQueueDeltas(compare),
    disdetteDeltas: buildDisdetteDeltas(compare),
    delta: deltas?.fatturato ?? null,
    deltas,
    deltaLabel: COMPARE_LABEL[compare],
    queue: queueRows,
    queueSummary: buildQueueSummary(queueRows),
    disdetteSummary: buildDisdetteSummaryItems(disdette),
    disdetteSegments: buildDisdettePoltronaSegments(disdette, poltrona),
    finEntrateBreakdown: buildFinEntrateBreakdown(period, poltrona),
    finUsciteBreakdown: buildFinUsciteBreakdown(uscite),
    giorni: scaleGiorni(
      GIORNI_BY_PERIOD[period] || GIORNI_BY_PERIOD['Questo mese'],
      poltronaRatios.bookings,
    ),
    rischioAbbandono: RISCHIO_BY_PERIOD[period] || RISCHIO_BY_PERIOD['Questo mese'],
    clientiPericolosi: PERICOLOSI_BY_PERIOD[period] || PERICOLOSI_BY_PERIOD['Questo mese'],
  };
};

export const formatEuro = (n) => {
  const value = Number(n);
  const isWhole = Number.isInteger(value) || Math.abs(value - Math.round(value)) < 0.005;
  return `€${value.toLocaleString('it-IT', {
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatDecimal = (n, digits = 1) =>
  Number(n).toLocaleString('it-IT', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

export const safeDivide = (numerator, denominator) =>
  denominator > 0 ? numerator / denominator : 0;

export const buildConicGradient = (data) => {
  let acc = 0;
  const stops = data.map(([, pct, color]) => {
    const start = acc;
    const end = acc + pct;
    acc = end;
    return `${COLOR_MAP[color]} ${start}% ${end}%`;
  });
  return `conic-gradient(${stops.join(', ')})`;
};

export const buildCategoryPoltronaTable = (items, segmentsMap) => {
  if (!items?.length || !segmentsMap) {
    return { columns: [], rows: [], cells: {} };
  }

  const columns = items.map(([label, , colorKey]) => ({
    key: label,
    label,
    color: COLOR_MAP[colorKey],
  }));

  const rows = (segmentsMap[items[0][0]] ?? []).map(({ id, name }) => ({ id, name }));
  const cells = rows.reduce((acc, row) => {
    acc[row.id] = columns.reduce((colAcc, col) => {
      const seg = segmentsMap[col.key]?.find((s) => s.id === row.id);
      colAcc[col.key] = seg != null ? `${seg.sharePct}%` : '—';
      return colAcc;
    }, {});
    return acc;
  }, {});

  return { columns, rows, cells };
};

export const buildQueuePoltronaTable = (queueRows) => {
  if (!queueRows?.length) {
    return { columns: [], rows: [], cells: {} };
  }

  const columns = queueRows.flatMap((row) => [
    { key: `${row.label}-queue`, label: row.label, subLabel: 'In coda', color: COLOR_MAP.queue },
    {
      key: `${row.label}-recovered`,
      label: row.label,
      subLabel: 'Recuperati',
      color: COLOR_MAP.recovered,
    },
  ]);

  const rows = (queueRows[0].queueSegments ?? []).map(({ id, name }) => ({ id, name }));
  const cells = rows.reduce((acc, pol) => {
    acc[pol.id] = {};
    queueRows.forEach((day) => {
      const qSeg = day.queueSegments?.find((s) => s.id === pol.id);
      const rSeg = day.recoveredSegments?.find((s) => s.id === pol.id);
      acc[pol.id][`${day.label}-queue`] = qSeg?.value ?? 0;
      acc[pol.id][`${day.label}-recovered`] = rSeg?.value ?? 0;
    });
    return acc;
  }, {});

  return { columns, rows, cells };
};

export const buildDisdettePoltronaTable = (disdetteSegments) => {
  if (!disdetteSegments) {
    return { columns: [], rows: [], cells: {} };
  }

  const columns = [
    { key: 'daCliente', label: 'Da cliente', color: COLOR_MAP.disdetteCliente },
    { key: 'daSalone', label: 'Da salone', color: COLOR_MAP.disdetteSalone },
  ];

  const rows = (disdetteSegments.daCliente ?? []).map(({ id, name }) => ({ id, name }));
  const cells = rows.reduce((acc, pol) => {
    acc[pol.id] = {
      daCliente: disdetteSegments.daCliente.find((s) => s.id === pol.id)?.value ?? 0,
      daSalone: disdetteSegments.daSalone.find((s) => s.id === pol.id)?.value ?? 0,
    };
    return acc;
  }, {});

  return { columns, rows, cells };
};
